/**
 * Modern Table Component
 * Reusable table component with modern design
 */

import React from 'react';
import './ModernTable.css';

const ModernTable = ({ 
  children, 
  className = '', 
  variant = 'default',
  striped = false,
  hover = false,
  ...props 
}) => {
  const tableClasses = [
    'modern-table',
    `modern-table--${variant}`,
    striped ? 'modern-table--striped' : '',
    hover ? 'modern-table--hover' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className="modern-table-container">
      <table className={tableClasses} {...props}>
        {children}
      </table>
    </div>
  );
};

const TableHeader = ({ children, className = '' }) => {
  return (
    <thead className={`modern-table__header ${className}`}>
      {children}
    </thead>
  );
};

const TableBody = ({ children, className = '' }) => {
  return (
    <tbody className={`modern-table__body ${className}`}>
      {children}
    </tbody>
  );
};

const TableRow = ({ 
  children, 
  className = '', 
  onClick,
  selected = false,
  ...props 
}) => {
  const rowClasses = [
    'modern-table__row',
    selected ? 'modern-table__row--selected' : '',
    onClick ? 'modern-table__row--clickable' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <tr 
      className={rowClasses}
      onClick={onClick}
      {...props}
    >
      {children}
    </tr>
  );
};

const TableCell = ({ 
  children, 
  className = '', 
  header = false,
  align = 'left',
  ...props 
}) => {
  const cellClasses = [
    'modern-table__cell',
    header ? 'modern-table__cell--header' : '',
    `modern-table__cell--${align}`,
    className
  ].filter(Boolean).join(' ');

  const CellComponent = header ? 'th' : 'td';

  return (
    <CellComponent className={cellClasses} {...props}>
      {children}
    </CellComponent>
  );
};

const TableHeaderCell = ({ 
  children, 
  className = '', 
  sortable = false,
  sortDirection = null,
  onSort,
  align = 'left',
  ...props 
}) => {
  const headerClasses = [
    'modern-table__header-cell',
    sortable ? 'modern-table__header-cell--sortable' : '',
    sortDirection ? `modern-table__header-cell--${sortDirection}` : '',
    `modern-table__header-cell--${align}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <th 
      className={headerClasses}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="modern-table__header-content">
        <span className="modern-table__header-text">{children}</span>
        {sortable && (
          <span className="modern-table__sort-icon">
            {sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '↕'}
          </span>
        )}
      </div>
    </th>
  );
};

const TableFooter = ({ children, className = '' }) => {
  return (
    <tfoot className={`modern-table__footer ${className}`}>
      {children}
    </tfoot>
  );
};

const TablePagination = ({ 
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  totalItems,
  onPageSizeChange,
  className = ''
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageSizeChange = (e) => {
    onPageSizeChange(parseInt(e.target.value));
  };

  return (
    <div className={`modern-table__pagination ${className}`}>
      <div className="modern-table__pagination-info">
        <span>
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
        <select 
          value={pageSize} 
          onChange={handlePageSizeChange}
          className="modern-table__page-size-select"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>
      
      <div className="modern-table__pagination-controls">
        <button
          className="modern-table__pagination-btn"
          onClick={handlePrevious}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        <div className="modern-table__pagination-pages">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                className={`modern-table__pagination-page ${
                  pageNum === currentPage ? 'modern-table__pagination-page--active' : ''
                }`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        
        <button
          className="modern-table__pagination-btn"
          onClick={handleNext}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const TableEmpty = ({ 
  message = 'No data available',
  icon = '📊',
  className = ''
}) => {
  return (
    <div className={`modern-table__empty ${className}`}>
      <div className="modern-table__empty-icon">{icon}</div>
      <div className="modern-table__empty-message">{message}</div>
    </div>
  );
};

const TableLoading = ({ 
  message = 'Loading...',
  className = ''
}) => {
  return (
    <div className={`modern-table__loading ${className}`}>
      <div className="modern-table__loading-spinner"></div>
      <div className="modern-table__loading-message">{message}</div>
    </div>
  );
};

// Export all components
export {
  ModernTable,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
  TableFooter,
  TablePagination,
  TableEmpty,
  TableLoading
};
